/**
 * Ecomyras — Bouton "Ajouter à la wishlist" pour les product cards et la fiche produit.
 *
 * Complète la fonctionnalité additive de assets/wishlist.js (qui gère déjà
 * l'icône du header, le badge animé et le tiroir) en ajoutant le seul
 * morceau qui manquait : un bouton cœur cliquable sur les produits
 * eux-mêmes, pour réellement ajouter/retirer un produit de la wishlist.
 *
 * Import ES relatif : les fichiers du dossier assets/ sont servis à plat
 * depuis le même dossier sur le CDN Shopify, donc './wishlist.js' résout
 * correctement vers /assets/wishlist.js sans configuration supplémentaire.
 */
import { isInWishlist, toggleWishlist } from './wishlist.js';

const WISHLIST_UPDATED_EVENT = 'wishlist:updated';

/**
 * Custom element <product-wishlist-button>.
 * Un exemplaire par bouton affiché (product card ou fiche produit) ;
 * ils restent synchronisés entre eux via l'event `wishlist:updated`.
 */
class ProductWishlistButton extends HTMLElement {
  connectedCallback() {
    this.handle = this.getAttribute('data-product-handle');
    this.button = this.querySelector('[data-wishlist-toggle]');
    if (!this.button || !this.handle) return;

    this._onClick = this._onClick.bind(this);
    this._onWishlistUpdate = this._onWishlistUpdate.bind(this);

    this.button.addEventListener('click', this._onClick);
    document.addEventListener(WISHLIST_UPDATED_EVENT, this._onWishlistUpdate);

    // État initial (silencieux, sans animation) d'après le localStorage.
    this._render(isInWishlist(this.handle), false);
  }

  disconnectedCallback() {
    document.removeEventListener(WISHLIST_UPDATED_EVENT, this._onWishlistUpdate);
  }

  /** @param {MouseEvent} event */
  _onClick(event) {
    event.preventDefault();
    event.stopPropagation(); // évite de déclencher le lien de la product card
    const nowActive = toggleWishlist(this.handle);
    this._render(nowActive, true);
  }

  _onWishlistUpdate() {
    // Garde ce bouton synchronisé si le produit a été (dé)wishlisté
    // ailleurs sur la page (ex: depuis le tiroir wishlist du header).
    this._render(isInWishlist(this.handle), false);
  }

  /**
   * @param {boolean} isActive
   * @param {boolean} animate
   */
  _render(isActive, animate) {
    this.button.setAttribute('aria-pressed', String(isActive));
    this.classList.toggle('is-active', isActive);

    if (!animate) return;

    this.classList.remove('is-animating');
    // Force le reflow pour pouvoir relancer l'animation sur des clics successifs.
    void this.offsetWidth;
    this.classList.add('is-animating');
  }
}

if (!customElements.get('product-wishlist-button')) {
  customElements.define('product-wishlist-button', ProductWishlistButton);
}
